import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import {
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup
} from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { AuthService } from 'src/app/services/auth.service';
import { TokenStorageService } from 'src/app/services/token-storage.service';
import { CustomValidators } from 'src/app/_helpers/customValidators';
import { CommonModule } from '@angular/common';
import { EskPopoverDirective } from '../../directives/esk-popover.directive';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css'],
  standalone: true,
  imports: [
    ReactiveFormsModule,
    CommonModule,
    RouterModule,
    EskPopoverDirective
  ]
})
export class LoginComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private authService = inject(AuthService);
  private tokenService = inject(TokenStorageService);

  loginForm: UntypedFormGroup;
  isLoading = false;
  errorMessage = '';
  showPassword = false;
  guestPopoverTxt = `Using the Farm and Forest Mapper as a Guest will give you unlimited access to all the
  functionality of the system but only for a day - after this time, any properties, paddocks or fences you have created
  will be lost. If you would like to keep your mapped features long term for ongoing use, please use the 'Register Now'
  option to create an account against which all your mapping can be saved.`;

  ngOnInit(): void {
    this.loginForm = new UntypedFormGroup({
      email: new UntypedFormControl(null, [
        CustomValidators.required,
        CustomValidators.email
      ]),
      password: new UntypedFormControl(null, CustomValidators.required)
    });
  }

  ngOnDestroy(): void {
    // Cleanup if needed
  }

  onSubmit(): void {
    if (this.loginForm.invalid) {
      this.markFormGroupTouched();
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';

    let credentials = {
      email: this.loginForm.get('email').value,
      password: this.loginForm.get('password').value
    };

    this.authService.login(credentials).subscribe({
      next: (data) => {
        this.isLoading = false;
        this.tokenService.saveToken(data.accessToken);
        this.tokenService.saveUser(data.user);
        this.router.navigate(['']);
      },
      error: (error: Error) => {
        this.isLoading = false;
        this.errorMessage = error.message || 'Login failed. Please try again.';
        // Focus on password field for better UX
        this.loginForm.get('password').markAsTouched();
      }
    });
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  private markFormGroupTouched(): void {
    Object.keys(this.loginForm.controls).forEach((key) => {
      const control = this.loginForm.get(key);
      control.markAsTouched();
    });
  }

  getFieldError(fieldName: string): string {
    const field = this.loginForm.get(fieldName);
    if (field && field.invalid && field.touched) {
      if (field.errors?.['required']) {
        return `${
          fieldName.charAt(0).toUpperCase() + fieldName.slice(1)
        } is required`;
      }
      if (field.errors?.['email']) {
        return 'Please enter a valid email address';
      }
    }
    return '';
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.loginForm.get(fieldName);
    return field && field.invalid && field.touched;
  }

  useAsGuest($event) {
    $event.preventDefault();
    console.log('use as guest login');

    this.authService.guestLogin().subscribe({
      next: (data) => {
        this.isLoading = false;
        this.tokenService.saveToken(data.token);
        this.tokenService.saveUser(data.user);
        this.router.navigate(['']);
      },
      error: (error: Error) => {
        this.isLoading = false;
        this.errorMessage = error.message || 'Login failed. Please try again.';
        // Focus on password field for better UX
        this.loginForm.get('password').markAsTouched();
      }
    });
  }
}
